import ProjectSession from "../project/project-session"
import ProjectView from "./project-view"
import ProjectLocalSettings from "../../manager/project/project-local-setting"
import {ViewType} from "../../gd/const/views/view-type"
import {VIEWS_SORT_POSITIONS} from "../../gd/const/views/view-type-config"
import Collection from "../../data-structures/collection"
import {SortDirection} from "../../data-structures/sort"
import projectViews_getProjectDefaultView from "./collection/get-default-view"
import projectView_create from "./actions/create"
import projectView_duplicate, {ProjectViewDuplicateParams} from "./actions/duplicate"
import projectView_delete from "./actions/delete"
import projectViews_shouldProjectViewBeRemoved from "./collection/filter/filter-project-view"
import ProjectViewFactory from "./factory"
import ProjectViewsLookup from "../project/inheritance/project-views-lookup"
import {ProjectViewAccess} from "./@const"
import projectViews_normalize from "./collection/normalize-config"
import {ProjectViewConfig} from "./@types";
import projectView_deleteAll from "./actions/delete-all"
import {ITEM_VIEW_PROJECT_INFO} from "../../gd/app-manager/item-view"
import ArrayUtils from "../../gd/utils/array"

const debug = false
// const debug = "ProjectViews"

export default class ProjectViews extends Collection<ProjectView> {

    public project              : ProjectSession

    public currentView!         : ProjectView

    public projectViewsLookup?  : ProjectViewsLookup

    constructor(project:ProjectSession) {
        super([])
        debug && console.log(debug, "constructor", project)

        this.project = project
    }

    // new ProjectLoader(projectId) should be initiated before initByProjectId
    // or any other project settings load function should be called
    // to make sure all necessary projects settings are loaded.
    //
    // There is also a problem to use async here because this method is used in ProjectViewsLookup
    // which is used in toJSON functions which can't be async
    static initByProjectId(projectId:ID):ProjectViews {

        const projectSession : ProjectSession = gd.session.projects.get(projectId)!
        const projectViews = new ProjectViews(projectSession)
        projectViews.resetByProjectId(projectId)

        return projectViews
    }

    resetByProjectId(projectId: ID):void {

        debug && console.log(debug, "resetByProjectId() projectId=", projectId)

        const projectSession : ProjectSession | null = gd.session.projects.get(projectId)

        if (!projectSession) {
            throw Error("#3984723hd no project found")
        }

        this.projectViewsLookup = new ProjectViewsLookup(projectSession.id)
        const viewsConfig : ProjectViewConfig[] = this.projectViewsLookup.get()

        debug && console.log(debug, "    viewsConfig=", viewsConfig)

        // const collection = new ProjectViews(projectSession)

        const models: ProjectView[] = []
        _.map(viewsConfig, (projectViewData: any) => {
            models.push(ProjectViewFactory(projectViewData, this))
        })

        this.reset(models)

        projectViews_normalize(this)

        this.filter()
        this.sort()

        if (this.currentView) {
            const view = this.get(this.currentView.id)
            if (view) this.setCurrentView(view)
        }

        debug && console.log(debug, 'updateViews', this.currentView, this)
    }

    async onProjectSettingsChanged(projectId: ID) {
        // esli drugoi project - ignore
        // or if project is view in structure - ignore

        if (this.project.isReport) return

        if (this.project.id !== projectId || this.project.isView) return

        this.resetByProjectId(projectId)

        const projectInfoItemView = gd.itemView.findByContainerId(ITEM_VIEW_PROJECT_INFO)

        // check if project info view is not opened and
        // if current view is deleted just go to default view
        if (!projectInfoItemView && !this.get(this.currentView?.id)) {
            this.getDefaultView().open()
        }
    }

    getNewId(): ID {
        return gd.utils.generateUIDWithCollisionChecking(_.keys(this.byId))
    }

    getByUrl(url: string): ProjectView {
        const urlParts = url.split('/')

        let view
        if (urlParts.length === 3) { // url has no view id
            view = _.find(this.models, (view)=>view.url.includes(url))
        } else {
            view = _.find(this.models, (view)=>view.url === url)
        }

        return view || this.getDefaultView()
    }

    getBySystemType(viewType: ViewType): ProjectView[] {
        return _.filter(this.models, (view)=>view.systemType === viewType)
    }

    getFirst(): ProjectView {
        return this.models[0]
    }

    getLast(): ProjectView {
        return this.models[this.models.length -1]
    }

    moveLeft(projectView: ProjectView) {
        const orderedIds    = _.map(this.models, model=>model.id)
        const oldIndex      = _.indexOf(orderedIds, projectView.id)
        let newIndex        = oldIndex - 1
        if (newIndex < 0) {
            newIndex = 0
        }

        ArrayUtils.moveInArrayMutate(this.models, oldIndex, newIndex)

        _.map(this.models, (item, key)=>{item.sortPosition = key})

        // this.sort()
        this.save()
    }

    moveRight(projectView: ProjectView) {
        const orderedIds    = _.map(this.models, model=>model.id)
        const oldIndex      = _.indexOf(orderedIds, projectView.id)
        let newIndex        = oldIndex + 1
        if (newIndex > orderedIds.length - 1) {
            newIndex = orderedIds.length - 1
        }

        ArrayUtils.moveInArrayMutate(this.models, oldIndex, newIndex)

        _.map(this.models, (item, key)=>{item.sortPosition = key})

        // this.sort()
        this.save()
    }

    setCurrentView(view: ProjectView) {
        this.currentView = view

        if (this.currentView.systemType !== ViewType.PROJECT_NO_VIEWS) {
            // save to local settings (Remember view)
            const localSettings = new ProjectLocalSettings(this.project.id)
            localSettings.defaultView = this.currentView.id
            localSettings.save()
        }
    }

    getDefaultView(checkLocalSettings: boolean = gd.user.clientData.labs.navigation.projectGoToRecentView): ProjectView {
        return projectViews_getProjectDefaultView(this, checkLocalSettings)
    }

    setDefaultView(view:any) {
        if (this.project.settings) {
            this.project.settings.defaultView = view.id
            this.project.settings.save()
        }
    }

    // special list of views available for customers
    getCustomerViews() {
        const viewsSystemTypes = _(this.models).map(view=>view.systemType).uniq().value()

        // get only views that are present in project settings
        return _(this.models)
            .filter((projectView:any)=>
                projectView.access === ProjectViewAccess.PUBLIC &&      // only public views are allowed to be added
                viewsSystemTypes.includes(projectView.systemType)       // only views that are pinned or subviews of system pinned views are allowed to be added
            )
            .value()
    }

    get isCustom() {
        return this.projectViewsLookup?.isCustom()
    }

    get isInherited() {
        return this.projectViewsLookup?.isInherited()
    }

    // remove all views? as there is no more system views
    // posle etogo po idee on inherite zanbovo ili pustoi budet
    async resetCustomViews() {
        if (this.project.settings) {
            this.project.settings.views = null
            this.project.settings.save()
        }
    }

    // reset views' app settings to default (delete config)
    async resetAppSettings(view:ProjectView) {
        // projectView.appSettings = false
        view.saveAppSettings(null)
        await this.save()

        // we also need to delete local settings for this view (!)
        // we cannot deal with this.currentView because current view can be different from item we reset for
        const projectLocalSettings = new ProjectLocalSettings(this.project.id)
        projectLocalSettings.deleteView(view.id)

        gd.getApp().start()
    }

    async deleteAll() {
        return projectView_deleteAll(this)
    }

    async createView(viewConfig:ProjectViewConfig) {
        return projectView_create(this, viewConfig)
    }

    async duplicate(view:ProjectView, params: ProjectViewDuplicateParams) {
        await projectView_duplicate(this, view, params)
    }

    async delete(view:ProjectView) {
        await projectView_delete(this, view)
    }

    // remove views that are not allowed due to access, modules or other restrictions
    filter() {
        const viewsToRemove = _.filter(this.models, (view:ProjectView)=>projectViews_shouldProjectViewBeRemoved(this, view))

        debug && console.log(debug, "filter viewsToRemove", viewsToRemove)

        // remove call is required to clear this.byId too
        _.map(viewsToRemove, (view: ProjectView)=>{
            this.remove(view.id)
        })
    }

    sort() {
        this.models = _(this.models)
            .orderBy([
                projectView => !_.isNil(projectView.sortPosition) ? 0 : 1,
                projectView => projectView.sortPosition,
                projectView => _.indexOf(VIEWS_SORT_POSITIONS, projectView.systemType)
            ], [
                SortDirection.ASC,
                SortDirection.ASC,
                SortDirection.ASC
            ])
            .map((model, key)=>{
                model.sortPosition = key
                return model
            })
            .value()
    }

    exportJsonInheritance() {
        const res: any = []

        _.map(this.models, view=>{
            if (view.canBeInherited) {
                res.push(view.toJsonInheritance())
            }
        })

        return res
    }

    setSharedViewId(projectView:ProjectView, id:string) {
        projectView.sharedViewId = id
        this.save()
    }

    // get views available to overwrite
    getSaveAsOptions(): ProjectView[] {
        const viewType = this.currentView!.systemType
        // find matching view systemType and it should not be readOnly
        return _.filter(this.models, view=>viewType === view.systemType && !view.acl.isReadonly)
    }

    saveAs(newAppSettingsConfig:any) {
        debug && console.log(debug, "saveAs", newAppSettingsConfig)

        return new Promise((resolve, reject) => {
            gd.modals.project.projectViewSaveAs((values:any)=>{
                const targetView = this.get(values.selectedId)
                if (!targetView) return

                targetView.saveAppSettings(newAppSettingsConfig)
                // this.save()

                gd.goTo(targetView.url)
                resolve(values.selectedId)
            })
        })
    }

    async save() {
        const config = _(this.models)
            .map((projectView:ProjectView) => {
                const viewConfig = projectView.toJson()

                // if project view is inherited save it to the config
                if (projectView.isInherited) {
                    viewConfig.isInherited = projectView.isInherited
                }
                return viewConfig
            })
            .value()

        debug && console.log(debug, "save", config)

        if (this.project.settings) {
            this.project.settings.views = config
            await this.project.settings.save()
        }

        // gd.bus.triggerEvent(SESSION_PROJECT_SETTINGS_CHANGE, this.project.id)
    }
}
